import { Injectable } from '@angular/core';
import { ReplaySubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class GoogleMapsLoaderService {
  private loaded = false;
  private loadPromise: Promise<void> | null = null;
  private loadedSubject = new ReplaySubject<boolean>(1);

  loaded$ = this.loadedSubject.asObservable();

  // The `geocoding` library (google.maps.Geocoder, used by the Users map's
  // radius-from-address search) is requested on every load — including the
  // PA-session bootstrap load — so the internal Users-map dialog never needs a
  // second Maps load just to pick up a library the first load omitted.
  private static readonly LIBRARIES = 'geocoding';

  load(apiKey: string): Promise<void> {
    if (this.loaded) {
      return Promise.resolve();
    }
    if (this.loadPromise) {
      return this.loadPromise;
    }

    // A maps script tag being present does NOT mean the API is ready — the tag
    // could still be loading or have previously failed. Only short-circuit when
    // `window.google.maps` is actually available (the fully-loaded happy path,
    // e.g. the PA-session bootstrap already loaded it). Otherwise fall through
    // and (re)load rather than resurrecting a dead/incomplete tag.
    const existingScript = document.querySelector(
      'script[src*="maps.googleapis.com"]'
    );
    if (existingScript && (window as any).google?.maps) {
      this.loaded = true;
      this.loadedSubject.next(true);
      return Promise.resolve();
    }

    this.loadPromise = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=${GoogleMapsLoaderService.LIBRARIES}`;
      script.async = true;
      script.onload = () => {
        this.loaded = true;
        this.loadedSubject.next(true);
        resolve();
      };
      script.onerror = () => {
        // Remove the dead tag so a retry doesn't hit the existingScript branch
        // and find a failed script masquerading as a loaded one.
        script.remove();
        this.loadPromise = null;
        reject(new Error('Google Maps script failed to load'));
      };
      document.head.appendChild(script);
    });

    return this.loadPromise;
  }

  isLoaded(): boolean {
    return this.loaded;
  }
}
